import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    Injector,
    input,
    output,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { IChecklistTimeInterval, IChecklistTimeIntervalData } from '../../../../../checklist-v2';
import { FiChecklistTimeIntervalType } from '@fi-shared/checklist/schedule/fi-checklist-time-interval-type';
import { FiChecklistTimeIntervalDataType } from '@fi-shared/checklist/schedule/fi-checklist-time-interval-data-type';
import { AppTextService } from '@fi-shared/app-text';
import { FiChecklist2ScheduleFormatService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-format.service';
import { FiChecklist2ScheduleIntervalService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-interval.service';
import { isDefined } from '@fi-shared/utils';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { FiChecklistScheduleType } from '@fi-shared/checklist/schedule/schedule-edit/fi-checklist-schedule-type';

@Component({
    selector: 'fi-checklist2-time-interval-settings',
    templateUrl: './time-interval-settings.component.html',
    styleUrls: ['./time-interval-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-time-interval-settings' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiChecklist2TimeIntervalSettingsComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiChecklist2TimeIntervalSettingsComponent,
        },
    ],
    standalone: false,
})
export class FiChecklist2TimeIntervalSettingsComponent extends ControlValueAccessorValidatorBase<IChecklistTimeInterval> {
    public intervalType = input.required<FiChecklistTimeIntervalType>();
    public scheduleType = input.required<FiChecklistScheduleType>();
    public isStartTimeDisabled = input<boolean>();
    public isEnabledInterval = input<boolean>();
    public isRecurringSchedule = input<boolean>();
    public isRemoveButtonShown = input<boolean>();
    public previousIntervalGroup = input<IChecklistTimeInterval[]>();

    public scheduleTimeIntervalIndex = input<number>();
    public isMonthlyInterval = input<boolean>();
    public canCopyToNext = input<boolean>();
    public canCopyToAll = input<boolean>();
    public isHighlightedTimeInput = input<boolean>();

    public removeInterval = output<void>();
    public addInterval = output<void>();
    public copyToNext = output<void>();
    public copyToAll = output<void>();

    protected disabledEndIntervalDataTypes = signal<FiChecklistTimeIntervalDataType[]>([]);

    private appTextService = inject(AppTextService);
    private fiChecklist2ScheduleIntervalService = inject(FiChecklist2ScheduleIntervalService);
    private fiChecklist2ScheduleFormatService = inject(FiChecklist2ScheduleFormatService);

    protected getMonthId = computed(() =>
        this.isMonthlyInterval() ? this.scheduleTimeIntervalIndex() + 1 : undefined,
    );

    protected isAuditOrOnDemandInterval = computed(() =>
        this.fiChecklist2ScheduleIntervalService.isAuditOrOnDemandInterval(this.intervalType()),
    );
    protected isAuditInterval = computed(() =>
        this.fiChecklist2ScheduleIntervalService.isAuditInterval(this.intervalType()),
    );
    protected isDailyInterval = computed(() =>
        this.fiChecklist2ScheduleIntervalService.isDailyInterval(this.intervalType()),
    );

    protected closeLabel = computed(() =>
        this.appTextService.get(
            this.isAuditOrOnDemandInterval() ? 'checklist.schedule_close_checklist_after' : 'common.close',
        ),
    );

    protected auditOnDemandLabel = computed(() =>
        this.appTextService.get(
            this.isAuditInterval() ? 'checklist.schedule_interval_audit' : 'checklist.schedule_interval_on_demand',
        ),
    );
    protected endTimeMinutesOffset = computed(() =>
        this.scheduleType() !== FiChecklistScheduleType.OnDemand ? 30 : 1,
    );

    constructor(injector: Injector) {
        super(injector);
    }

    protected getTimeLimitByPrevDayTime(): string {
        const previousIntervalGroup = this.previousIntervalGroup() ?? [];
        const endTimeMinutesOffset = this.endTimeMinutesOffset();
        return this.fiChecklist2ScheduleFormatService.getTimeLimitByPrevDayTime(
            previousIntervalGroup,
            endTimeMinutesOffset,
        );
    }

    protected recalculateIntervalEnd(): void {
        const minAvailableEndIntervalMS: number = this.fiChecklist2ScheduleIntervalService
            .getDateWithGapByInterval(this.ngModel.Start)
            .getTime();
        const endIntervalMS: number = this.fiChecklist2ScheduleIntervalService
            .convertIntervalTimeDateToDate(this.ngModel.End)
            .getTime();
        if (endIntervalMS < minAvailableEndIntervalMS) {
            this.ngModel.End = this.fiChecklist2ScheduleIntervalService.convertDateToIntervalTimeData(
                new Date(minAvailableEndIntervalMS),
            );
            this.doPropagateChanges();
        }
    }

    protected getTimeLimitByTimeIntervalType(isMax: boolean, minutesOffset?: number): string {
        const basedIntervalData: IChecklistTimeIntervalData = isMax ? this.ngModel.End : this.ngModel.Start;

        let timeLimitStr: string;
        if (isMax && this.ngModel.End.IsNextDay) {
            timeLimitStr = this.fiChecklist2ScheduleFormatService.getHourMinuteInputData(
                { Hour: 24 + basedIntervalData.Hour, Minute: basedIntervalData.Minute },
                minutesOffset,
            );
        } else {
            timeLimitStr = this.fiChecklist2ScheduleFormatService.getHourMinuteInputData(
                basedIntervalData,
                minutesOffset,
            );
        }

        if (timeLimitStr === '') {
            return undefined;
        }

        switch (this.intervalType()) {
            case FiChecklistTimeIntervalType.RecurringDaily:
                return timeLimitStr;
            case FiChecklistTimeIntervalType.RecurringWeekly:
                return this.ngModel.Start.DayOfWeek === this.ngModel.End.DayOfWeek ? timeLimitStr : undefined;
            case FiChecklistTimeIntervalType.RecurringMonthly:
                return this.isMonthlyIntervalTimeLimited(this.ngModel) ? timeLimitStr : undefined;
            case FiChecklistTimeIntervalType.RecurringYearly:
                return this.ngModel.Start.Month === this.ngModel.End.Month &&
                    this.ngModel.Start.Day === this.ngModel.End.Day
                    ? timeLimitStr
                    : undefined;
        }
    }

    private isMonthlyIntervalTimeLimited(interval: IChecklistTimeInterval): boolean {
        if (!this.isDataTypesEqual(interval)) {
            return false;
        }

        switch (interval.Start.DataType) {
            case FiChecklistTimeIntervalDataType.Day:
                return interval.Start.Day === interval.End.Day;
            case FiChecklistTimeIntervalDataType.LastWeekDay:
            case FiChecklistTimeIntervalDataType.FirstWeekDay:
                return interval.End.DayOfWeek === interval.Start.DayOfWeek;
            case FiChecklistTimeIntervalDataType.LastDay:
                return true;
        }
    }

    protected getHourMinuteInputData(intervalData: IChecklistTimeIntervalData): string {
        return this.fiChecklist2ScheduleFormatService.getHourMinuteInputData(intervalData);
    }

    protected isHourMinuteDefined(): boolean {
        return (
            isDefined(this.ngModel.Start.Hour) &&
            isDefined(this.ngModel.Start.Minute) &&
            isDefined(this.ngModel.End.Hour) &&
            isDefined(this.ngModel.End.Minute)
        );
    }

    protected onTimeInputChange(
        intervalData: IChecklistTimeIntervalData,
        isStartInterval: boolean,
        newTime: string,
    ): void {
        intervalData = this.fiChecklist2ScheduleFormatService.getIntervalDataOnTimePickerChange(intervalData, newTime);
        if (isStartInterval) {
            this.ngModel.Start = intervalData;
        } else {
            this.ngModel.End = intervalData;
        }
        this.doPropagateChanges();
    }

    private isDataTypesEqual(interval: IChecklistTimeInterval): boolean {
        return interval.Start.DataType === interval.End.DataType;
    }

    protected getWeekDayLimit(isMax: boolean): number {
        if (!this.isDataTypesEqual(this.ngModel)) {
            return undefined;
        }
        return isMax ? this.ngModel.End.DayOfWeek : this.ngModel.Start.DayOfWeek;
    }

    protected getDayOfMonthLimit(isMax: boolean): number {
        if (!this.isDataTypesEqual(this.ngModel)) {
            return undefined;
        }
        return isMax ? this.ngModel.End.Day : this.ngModel.Start.Day;
    }

    protected setDisabledEndIntervalDataTypes(): void {
        if (!isDefined(this.ngModel.Start.DataType)) {
            return;
        }

        const disabledEndIntervalDataTypes = this.invalidIntervalDataTypes[this.ngModel.Start.DataType];
        this.disabledEndIntervalDataTypes.set(disabledEndIntervalDataTypes);
        const isInvalidEndDataType: boolean = disabledEndIntervalDataTypes.includes(this.ngModel.End.DataType);

        if (
            this.isLastDayIntervalDataType(this.ngModel.Start.DataType) &&
            !this.isLastDayIntervalDataType(this.ngModel.End.DataType) &&
            isInvalidEndDataType
        ) {
            this.ngModel.End.DataType = FiChecklistTimeIntervalDataType.LastDay;
            delete this.ngModel.End.Day;
            delete this.ngModel.End.DayOfWeek;
        }

        if (isInvalidEndDataType) {
            this.resetIntervalData(this.ngModel.End, this.isLastDayIntervalDataType(this.ngModel.End.DataType));
        }

        this.doPropagateChanges();
    }

    private isLastDayIntervalDataType(dataType: FiChecklistTimeIntervalDataType): boolean {
        return dataType === FiChecklistTimeIntervalDataType.LastDay;
    }

    private resetIntervalData(intervalData: IChecklistTimeIntervalData, isLastDayIntervalDataType: boolean) {
        const skipResetPropertyNames: string[] = ['DataType', 'Month'];
        Object.keys(intervalData).forEach((key: string) => {
            if (isLastDayIntervalDataType && skipResetPropertyNames.includes(key)) {
                return;
            }

            intervalData[key] = undefined;
        });
    }

    protected readonly FiInfoIconMode = FiInfoIconMode;

    private readonly invalidIntervalDataTypes: {
        [key in FiChecklistTimeIntervalDataType]: FiChecklistTimeIntervalDataType[];
    } = {
        [FiChecklistTimeIntervalDataType.LastDay]: [
            FiChecklistTimeIntervalDataType.Day,
            FiChecklistTimeIntervalDataType.LastWeekDay,
            FiChecklistTimeIntervalDataType.FirstWeekDay,
        ],

        [FiChecklistTimeIntervalDataType.Day]: [
            FiChecklistTimeIntervalDataType.LastWeekDay,
            FiChecklistTimeIntervalDataType.FirstWeekDay,
        ],

        [FiChecklistTimeIntervalDataType.LastWeekDay]: [
            FiChecklistTimeIntervalDataType.Day,
            FiChecklistTimeIntervalDataType.FirstWeekDay,
        ],

        [FiChecklistTimeIntervalDataType.FirstWeekDay]: [],
    };
}
